import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormHelperText,
  Heading,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useBreakpointValue,
  Switch,
  Tooltip,
  Spinner,
  InputRightElement,
  Checkbox,
  Select,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EmailIcon, SearchIcon } from '@chakra-ui/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import AdminLayout from '../../layouts/AdminLayout';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { facrApi } from '../../services/facr/facrApi';
import {
  getNewsletterSubscribers,
  sendNewsletter,
  sendNewsletterTest,
  deleteSubscriber,
  toggleSubscriberStatus,
  NewsletterSubscriber,
  NewsletterSendData,
  getNewsletterStatus,
  sendNewsletterTestAdvanced,
  NewsletterTestType,
  sendNewsletterDigest,
  setNewsletterAutomation,
  previewNewsletter,
  DigestType,
  createNewsletterSubscriber,
  searchByICO,
  AresCustomerInfo,
} from '../../services/admin/newsletter';
import { getAdminSettings, updateAdminSettings, AdminSettings } from '../../services/settings';
import { adminSendSmtpTest, AdminSmtpTestPayload } from '../../services/admin/newsletter';
import { getRecentEmailStats, EmailStatRow, getEmailEventsForLog, EmailEventRow } from '../../services/admin/newsletter';
import { HelpTooltipCard } from '../../components/common/HelpTooltipCard';
import DateRangePicker from '../../components/common/DateRangePicker';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';

export default function NewsletterAdminPage() {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState<number[]>([]);
  const [newsletterData, setNewsletterData] = useState<NewsletterSendData>({
    subject: '',
    content: '',
  });

  // SMTP test (admin)
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState<number | ''>('');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpTo, setSmtpTo] = useState('');
  const [smtpTLS, setSmtpTLS] = useState(true);
  const [smtpSubject, setSmtpSubject] = useState('SMTP Test');
  const [smtpBody, setSmtpBody] = useState('<p>Toto je testovací e‑mail (admin SMTP debug).</p>');
  const [showSmtpPass, setShowSmtpPass] = useState(false);

  const adminSmtpTestMutation = useMutation({
    mutationFn: async () => {
      const payload: AdminSmtpTestPayload = {
        host: smtpHost.trim(),
        port: typeof smtpPort === 'number' ? smtpPort : 0,
        username: smtpUser || undefined,
        password: smtpPass || undefined,
        from: smtpFrom.trim(),
        to: smtpTo.trim(),
        subject: smtpSubject || undefined,
        body: smtpBody || undefined,
        use_tls: smtpTLS,
      };
      return adminSendSmtpTest(payload);
    },
    onSuccess: (res: any) => {
      if (res?.ok) {
        toast({ title: 'SMTP test úspěšný', description: res?.message || 'Test email sent', status: 'success' });
      } else {
        toast({ title: 'SMTP test selhal', description: res?.error || 'Chyba při odeslání testu', status: 'error' });
      }
    },
    onError: (err: any) => {
      toast({ title: 'Chyba požadavku', description: err?.response?.data?.error || err?.message, status: 'error' });
    }
  });
  const [testEmail, setTestEmail] = useState<string>('');
  const [testEmails, setTestEmails] = useState<string>('');
  const [testType, setTestType] = useState<NewsletterTestType>('newsletter');
  const [prefsModalOpen, setPrefsModalOpen] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState<NewsletterSubscriber | null>(null);
  const [editingPrefs, setEditingPrefs] = useState<Record<string, boolean>>({});
  // Send mode: custom body or digest template
  const [sendMode, setSendMode] = useState<'custom' | DigestType>('custom');
  const [competitions, setCompetitions] = useState<string>('');
  const [previewSubject, setPreviewSubject] = useState<string>('');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  type MailType = 'weekly' | 'matches' | 'scores' | 'blogs' | 'events';
  const mailTypeLabel: Record<MailType, string> = { weekly: 'Týdenní přehled', matches: 'Zápasy', scores: 'Výsledky', blogs: 'Novinky', events: 'Akce' };
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeType, setActiveType] = useState<MailType | null>(null);
  const [typePreview, setTypePreview] = useState<Record<string, { subject: string; html: string } | undefined>>({});
  const [detailsCompetitions, setDetailsCompetitions] = useState<string>('');
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [sendNowLoading, setSendNowLoading] = useState<boolean>(false);
  
  // Add subscriber state
  const [newSubscriberEmail, setNewSubscriberEmail] = useState('');
  const [newSubscriberICO, setNewSubscriberICO] = useState('');
  const [newSubscriberPrefs, setNewSubscriberPrefs] = useState<Record<string, boolean>>({
    weekly: true,
    matches: true,
    scores: true,
    blogs: true,
    events: true,
  });
  const [icoSearchResult, setIcoSearchResult] = useState<AresCustomerInfo | null>(null);
  const [icoSearching, setIcoSearching] = useState(false);
  
  const openDetails = (t: MailType) => { setActiveType(t); setDetailsOpen(true); };
  const closeDetails = () => { setDetailsOpen(false); setActiveType(null); setDetailsCompetitions(''); };

  // Helpers for competitions multi-select handling
  const selectedCompCodes = React.useMemo(() => {
    return new Set((competitions || '').split(',').map((s) => s.trim()).filter(Boolean));
  }, [competitions]);
  const toggleComp = (code: string, on: boolean) => {
    const next = new Set(selectedCompCodes);
    if (on) next.add(code); else next.delete(code);
    setCompetitions(Array.from(next).join(', '));
  };
  const clearComps = () => setCompetitions('');
  const selectAllComps = () => setCompetitions(compOptions.map(o => o.code).join(', '));

  const detailsSelectedCompCodes = React.useMemo(() => {
    return new Set((detailsCompetitions || '').split(',').map((s) => s.trim()).filter(Boolean));
  }, [detailsCompetitions]);
  const toggleDetailsComp = (code: string, on: boolean) => {
    const next = new Set(detailsSelectedCompCodes);
    if (on) next.add(code); else next.delete(code);
    setDetailsCompetitions(Array.from(next).join(', '));
  };
  const detailsClearComps = () => setDetailsCompetitions('');
  const detailsSelectAllComps = () => setDetailsCompetitions(compOptions.map(o => o.code).join(', '));

  // Fetch subscribers
  const { data: subscribers = [], isLoading } = useQuery({
    queryKey: ['admin', 'newsletter-subscribers'],
    queryFn: getNewsletterSubscribers,
  });

  const recipientsForType = (t: MailType): string[] => {
    const key = t === 'weekly' ? 'weekly' : t;
    return subscribers
      .filter((s: any) => s.is_active && s?.preferences && s.preferences[key] === true)
      .map((s: any) => s.email);
  };
  const getRecipientsFor = (t: MailType, comps?: string): string[] => {
    const key = t === 'weekly' ? 'weekly' : t;
    const base = (subscribers as any[]).filter((s: any) => s?.is_active && s?.preferences && s.preferences[key] === true);
    if (!comps || !comps.trim() || (t !== 'matches' && t !== 'scores')) {
      return base.map((s: any) => s.email);
    }
    const list = comps.split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
    const filtered = base.filter((s: any) => {
      const prefs: any = s?.preferences || {};
      const raw: string = typeof prefs.competitions === 'string' && prefs.competitions ? prefs.competitions : (typeof prefs.categories === 'string' ? prefs.categories : '');
      const arr = raw.split(',').map((x: string) => x.trim().toLowerCase()).filter(Boolean);
      if (arr.length === 0) return true;
      return arr.some((v: string) => list.includes(v));
    });
    return filtered.map((s: any) => s.email);
  };
  const exportRecipientsCSV = (t: MailType, comps?: string) => {
    const list = getRecipientsFor(t, comps);
    const safeCSV = (value: any) => {
      const s = String(value ?? '');
      return /^[=+\-@]/.test(s) ? `'${s}` : s;
    };
    const header = ['email','type','competitions'];
    const lines = [
      header.join(','),
      ...list.map(e => [safeCSV(e), t, (comps || '').trim()].join(','))
    ];
    const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter_recipients_${t}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const loadPreviewForType = async (t: MailType, comps?: string) => {
    const prefs: any = {};
    if (t === 'weekly') { prefs.blogs = true; prefs.events = true; prefs.matches = true; prefs.scores = true; }
    else { (prefs as any)[t] = true; }
    if (comps && comps.trim()) { prefs.competitions = comps.trim(); }
    const res = await previewNewsletter({ preferences: prefs });
    setTypePreview(prev => ({ ...prev, [t]: { subject: res.subject, html: res.html } }));
  };
  useEffect(() => {
    if (detailsOpen && activeType && !typePreview[activeType]) {
      (async () => {
        try {
          setDetailsLoading(true);
          await loadPreviewForType(activeType!, detailsCompetitions);
        } finally {
          setDetailsLoading(false);
        }
      })();
    }
  }, [detailsOpen, activeType]);

  // Prefetch preview subjects for status list when there are recipients
  useEffect(() => {
    const types: MailType[] = ['weekly','matches','scores','blogs','events'];
    (async () => {
      for (const t of types) {
        try {
          const count = recipientsForType(t).length;
          if (count > 0 && !typePreview[t]) {
            await loadPreviewForType(t);
          }
        } catch {}
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribers]);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const testModal = useDisclosure();
  const smtpModal = useDisclosure();
  const addSubscriberModal = useDisclosure();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Load club competitions for nicer dropdowns (FACR)
  const { data: publicSettings } = usePublicSettings();
  const clubId = publicSettings?.club_id;
  const clubType = (publicSettings?.club_type as 'football' | 'futsal') || 'football';
  const { data: clubCompetitions = [] } = useQuery({
    queryKey: ['facr', 'competitions', clubId, clubType],
    queryFn: async () => {
      if (!clubId) return [] as Array<{ code?: string; id?: string; name?: string }>;
      const comps = await facrApi.getClubCompetitions(clubId, clubType);
      return comps || [];
    },
    enabled: !!clubId,
  });
  const compOptions = (clubCompetitions as any[]).map((c) => ({
    code: String(c?.code || c?.id || ''),
    name: String(c?.name || c?.code || c?.id || ''),
  })).filter((o) => o.code);

  // Admin settings (for scheduling)
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: getAdminSettings,
  });
  const settings = settingsQuery.data as AdminSettings | undefined;
  const [enableWeekly, setEnableWeekly] = useState<boolean>(!!settings?.enable_weekly);
  const [enableMatchReminders, setEnableMatchReminders] = useState<boolean>(!!settings?.enable_match_reminders);
  const [enableResults, setEnableResults] = useState<boolean>(!!settings?.enable_results);
  const [weeklyDay, setWeeklyDay] = useState<AdminSettings['newsletter_weekly_day']>(settings?.newsletter_weekly_day || 'sun');
  const toTimeString = (h?: number) => (String(typeof h === 'number' ? Math.max(0, Math.min(23, h)) : 18).padStart(2, '0')) + ':00';
  const [weeklyTime, setWeeklyTime] = useState<string>(toTimeString(settings?.newsletter_weekly_hour as number | undefined));
  const [reminderLead, setReminderLead] = useState<number>(typeof settings?.newsletter_reminder_lead_hours === 'number' ? (settings!.newsletter_reminder_lead_hours as number) : 48);
  const [quietStartTime, setQuietStartTime] = useState<string>(toTimeString(settings?.newsletter_quiet_start as number | undefined));
  const [quietEndTime, setQuietEndTime] = useState<string>(toTimeString(settings?.newsletter_quiet_end as number | undefined));

  // Sync local state when settings load
  useEffect(() => {
    if (!settings) return;
    setEnableWeekly(!!settings.enable_weekly);
    setEnableMatchReminders(!!settings.enable_match_reminders);
    setEnableResults(!!settings.enable_results);
    setWeeklyDay(settings.newsletter_weekly_day || 'sun');
    setWeeklyTime(toTimeString(typeof settings.newsletter_weekly_hour === 'number' ? settings.newsletter_weekly_hour! : 18));
    setReminderLead(typeof settings.newsletter_reminder_lead_hours === 'number' ? settings.newsletter_reminder_lead_hours! : 48);
    setQuietStartTime(toTimeString(typeof settings.newsletter_quiet_start === 'number' ? settings.newsletter_quiet_start! : 22));
    setQuietEndTime(toTimeString(typeof settings.newsletter_quiet_end === 'number' ? settings.newsletter_quiet_end! : 7));
  }, [settings]);

  const parseHour = (t: string) => {
    const m = /^\s*(\d{1,2})(?::(\d{1,2}))?/.exec(t || '');
    const h = m ? parseInt(m[1], 10) : 18;
    return Math.max(0, Math.min(23, isNaN(h) ? 18 : h));
  };
  const saveScheduleMutation = useMutation({
    mutationFn: () => updateAdminSettings({
      enable_weekly: enableWeekly,
      enable_match_reminders: enableMatchReminders,
      enable_results: enableResults,
      newsletter_weekly_day: weeklyDay,
      newsletter_weekly_hour: parseHour(weeklyTime),
      newsletter_reminder_lead_hours: reminderLead,
      newsletter_quiet_start: parseHour(quietStartTime),
      newsletter_quiet_end: parseHour(quietEndTime),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      toast({ title: 'Nastavení rozesílek uloženo', status: 'success' });
    },
    onError: (err: any) => {
      toast({ title: 'Chyba při ukládání', description: err?.response?.data?.error || err?.message, status: 'error' });
    }
  });

  // Basic client-side sanitizer for preview (defense-in-depth; backend should still sanitize)
  const sanitizeHtml = (html: string): string => {
    try {
      // Remove script/style/iframe tags entirely
      const withoutDangerousTags = html.replace(/<\s*(script|style|iframe)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '');
      // Remove event handlers like onClick, onError, etc.
      const withoutEvents = withoutDangerousTags.replace(/ on[a-zA-Z]+\s*=\s*"[^"]*"/g, '').replace(/ on[a-zA-Z]+\s*=\s*'[^']*'/g, '').replace(/ on[a-zA-Z]+\s*=\s*[^\s>]+/g, '');
      // Neutralize javascript:, data: URLs in href/src
      const neutralizedUrls = withoutEvents.replace(/(href|src)\s*=\s*(["'])\s*(javascript:|data:)/gi, '$1=$2#');
      return neutralizedUrls;
    } catch {
      return '';
    }
  };

  // Filter subscribers based on search term
  const filteredSubscribers = subscribers.filter((subscriber) =>
    subscriber.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle subscriber status
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      toggleSubscriberStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter-subscribers'] });
      toast({
        title: 'Stav odběratele byl aktualizován',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  // Delete subscriber
  const deleteMutation = useMutation({
    mutationFn: deleteSubscriber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter-subscribers'] });
      setSelectedSubscribers([]);
      toast({
        title: 'Odběratel byl smazán',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const updatePrefsMutation = useMutation({
    mutationFn: ({ id, prefs }: { id: number; prefs: Record<string, boolean> }) =>
      // lazy import to avoid cyclic imports
      import('../../services/admin/newsletter').then((m) => m.updateSubscriberPreferences(id, prefs)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter-subscribers'] });
      setPrefsModalOpen(false);
      toast({ title: 'Preference uloženy', status: 'success' });
    },
    onError: (err: any) => {
      toast({ title: 'Chyba při ukládání preferencí', description: err?.response?.data?.error || err?.message, status: 'error' });
    },
  });

  // Send newsletter
  const sendNewsletterMutation = useMutation({
    mutationFn: sendNewsletter,
    onSuccess: () => {
      onClose();
      setNewsletterData({ subject: '', content: '' });
      toast({
        title: 'Newsletter byl odeslán',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Chyba při odesílání newsletteru',
        description: error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Došlo k chybě',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Send test newsletter
  const sendTestMutation = useMutation({
    mutationFn: () => {
      const emails = testEmails
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (emails.length > 0) {
        return sendNewsletterTestAdvanced({ emails, type: testType });
      }
      if (testEmail) {
        return sendNewsletterTestAdvanced({ email: testEmail, type: testType });
      }
      return sendNewsletterTestAdvanced({ type: testType });
    },
    onSuccess: (data: { message: string; recipient?: string; recipients?: string[]; type: string }) => {
      toast({
        title: 'Test odeslán',
        description:
          data?.recipients && data.recipients.length > 0
            ? `E-mail byl odeslán na ${data.recipients.join(', ')}`
            : data?.recipient
            ? `E-mail byl odeslán na ${data.recipient}`
            : 'Testovací e-mail byl odeslán',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setTestEmail('');
      setTestEmails('');
      testModal.onClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Chyba při odesílání testu',
        description: error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Došlo k chybě',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Create subscriber mutation
  const createSubscriberMutation = useMutation({
    mutationFn: ({ email, preferences }: { email: string; preferences?: Record<string, boolean> }) =>
      createNewsletterSubscriber(email, preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter-subscribers'] });
      toast({
        title: 'Odběratel byl přidán',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // Reset form
      setNewSubscriberEmail('');
      setNewSubscriberICO('');
      setIcoSearchResult(null);
      setNewSubscriberPrefs({
        weekly: true,
        matches: true,
        scores: true,
        blogs: true,
        events: true,
      });
      addSubscriberModal.onClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Chyba při přidávání odběratele',
        description: error?.response?.data?.error || error?.message || 'Došlo k chybě',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // ICO search mutation
  const icoSearchMutation = useMutation({
    mutationFn: searchByICO,
    onSuccess: (data) => {
      setIcoSearchResult(data);
      if (data.found && data.name) {
        // Auto-fill email if not already set
        if (!newSubscriberEmail.trim()) {
          setNewSubscriberEmail(data.name);
        }
      }
    },
    onError: (error: any) => {
      setIcoSearchResult(null);
      toast({
        title: 'ICO nebylo nalezeno',
        description: error?.response?.data?.error || error?.message || 'Došlo k chybě při vyhledávání',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    },
    onSettled: () => {
      setIcoSearching(false);
    },
  });

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedSubscribers(filteredSubscribers.map((s) => s.id));
    } else {
      setSelectedSubscribers([]);
    }
  };

  const handleSelectSubscriber = (id: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedSubscribers((prev) => [...prev, id]);
    } else {
      setSelectedSubscribers((prev) => prev.filter((subId) => subId !== id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSubscribers.length === 0) return;
    const ok = await confirm({
      title: 'Smazat vybrané odběratele',
      message: 'Opravdu chcete smazat vybrané odběratele?',
      confirmText: 'Smazat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;
    await Promise.all(selectedSubscribers.map((id) => deleteMutation.mutateAsync(id)));
  };

  const handleSendNewsletter = () => {
    sendNewsletterMutation.mutate(newsletterData);
  };

  // Handle ICO search with auto-trigger
  const handleICOChange = (value: string) => {
    setNewSubscriberICO(value);
    setIcoSearchResult(null);
    
    // Auto-search when ICO is 8 digits
    if (value.length === 8 && /^\d+$/.test(value)) {
      setIcoSearching(true);
      icoSearchMutation.mutate(value);
    }
  };

  // Handle creating subscriber
  const handleCreateSubscriber = () => {
    if (!newSubscriberEmail.trim()) {
      toast({
        title: 'Chybí e-mail',
        description: 'Zadejte e-mailovou adresu odběratele',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    createSubscriberMutation.mutate({
      email: newSubscriberEmail.trim(),
      preferences: newSubscriberPrefs,
    });
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'd. M. yyyy HH:mm', { locale: cs });
  };

  // Newsletter status
  const { data: statusData } = useQuery({
    queryKey: ['admin', 'newsletter-status'],
    queryFn: getNewsletterStatus,
  });
  const automationToggle = useMutation({
    mutationFn: (enabled: boolean) => setNewsletterAutomation(enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter-status'] }),
  });

  // Recent delivery stats
  const { data: recentStats = [], isLoading: statsLoading } = useQuery<EmailStatRow[]>({
    queryKey: ['admin', 'newsletter-stats-recent'],
    queryFn: getRecentEmailStats,
  });
  const [statsFrom, setStatsFrom] = useState<string>('');
  const [statsTo, setStatsTo] = useState<string>('');
  const filteredStats = React.useMemo(() => {
    if (!recentStats || recentStats.length === 0) return recentStats;
    return recentStats.filter((row) => {
      const ts = new Date(row.created_at).getTime();
      if (isNaN(ts)) return true;
      if (statsFrom) {
        const fromTs = new Date(statsFrom + 'T00:00:00').getTime();
        if (!isNaN(fromTs) && ts < fromTs) return false;
      }
      if (statsTo) {
        const toTs = new Date(statsTo + 'T23:59:59').getTime();
        if (!isNaN(toTs) && ts > toTs) return false;
      }
      return true;
    });
  }, [recentStats, statsFrom, statsTo]);

  // Events modal state
  const [eventsModalOpen, setEventsModalOpen] = useState(false);
  const [activeLog, setActiveLog] = useState<EmailStatRow | null>(null);
  const eventsQuery = useQuery<EmailEventRow[]>({
    queryKey: ['admin', 'email-events', activeLog?.id],
    queryFn: () => getEmailEventsForLog(activeLog?.id || 0),
    enabled: !!activeLog?.id && eventsModalOpen,
  });
  const [eventFilter, setEventFilter] = useState<{ open: boolean; click: boolean; spam: boolean; unsubscribe: boolean }>({ open: true, click: true, spam: true, unsubscribe: true });
  const filteredEvents = (eventsQuery.data || []).filter((ev) => eventFilter[(ev.event_type as 'open'|'click'|'spam'|'unsubscribe')] ?? true);
  const { confirm } = useConfirmDialog();

  const exportCSV = () => {
    // Prevent CSV injection by prefixing dangerous leading characters
    const safeCSV = (value: any) => {
      const s = String(value ?? '');
      return /^[=+\-@]/.test(s) ? `'${s}` : s;
    };
    const rows = (eventsQuery.data || []).map((ev) => ({
      id: ev.id,
      created_at: ev.created_at,
      event_type: ev.event_type,
      url: ev.meta?.url ?? '',
      ua: ev.meta?.ua ?? '',
      ip: ev.meta?.ip ?? '',
    }));
    const header = ['id','created_at','event_type','url','ua','ip'];
    const lines = [
      header.join(','),
      ...rows.map(r => [
        safeCSV(r.id),
        safeCSV(r.created_at),
        safeCSV(r.event_type),
        safeCSV(JSON.stringify(r.url)),
        safeCSV(JSON.stringify(r.ua)),
        safeCSV(JSON.stringify(r.ip))
      ].join(','))
    ];
    const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email_events_${activeLog?.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <Box p={6}>
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" mb={2}>
              Správa newsletteru
            </Heading>
            <Text color="gray.600">
              Spravujte odběratele newsletteru a rozesílejte hromadné zprávy
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button
              colorScheme="green"
              leftIcon={<AddIcon />}
              onClick={addSubscriberModal.onOpen}
            >
              Přidat odběratele
            </Button>
            {selectedSubscribers.length > 0 && (
              <Button
                colorScheme="red"
                variant="outline"
                leftIcon={<DeleteIcon />}
                onClick={handleDeleteSelected}
                isLoading={deleteMutation.isLoading}
              >
                Smazat vybrané ({selectedSubscribers.length})
              </Button>
            )}
            <Button
              variant="outline"
              onClick={testModal.onOpen}
            >
              Odeslat testovací e-mail
            </Button>
            <Button variant="outline" onClick={smtpModal.onOpen}>Otestovat SMTP</Button>
            <Button
              colorScheme="blue"
              leftIcon={<EmailIcon />}
              onClick={onOpen}
              isDisabled={subscribers.length === 0}
            >
              Odeslat newsletter
            </Button>
          </HStack>
        </Flex>

        {/* Tabs layout */}
        <Tabs colorScheme="blue" isFitted>
          <TabList>
            <Tab>Stav</Tab>
            <Tab>Plánování</Tab>
            <Tab>Statistika</Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              {/* Status panel */}
              <Box bg={cardBg} borderRadius="lg" boxShadow="sm" p={4} mb={6}>
                <HStack justify="space-between" align="center" mb={3}>
                  <Heading size="md">Stav rozesílek</Heading>
                  <HelpTooltipCard
                    label="Jak funguje automatika"
                    title="Stav rozesílek a automatické odesílání"
                    items={[
                      'Zobrazuje, kolik odběratelů má newsletter aktivní a jak často běží automatická úloha.',
                      'Přepínač „Automatické rozesílky“ zapíná/vypíná celou automatiku – harmonogram v záložce Plánování musí být také nastaven.',
                      'Další běh a příklady příjemců vám pomohou ověřit, že se e‑maily budou posílat správné skupině lidí.',
                    ]}
                  />
                </HStack>
                <HStack spacing={6} wrap="wrap">
                  <Text><b>Odběratelů:</b> {statusData?.total_subscribers ?? '—'}</Text>
                  <Text><b>Aktivních:</b> {statusData?.active_subscribers ?? '—'}</Text>
                  <Text><b>Další běh:</b> {statusData?.next_approximate ? format(new Date(statusData.next_approximate), 'd. M. yyyy HH:mm', { locale: cs }) : '—'}</Text>
                  <Text><b>Interval:</b> {statusData?.interval_minutes ? `${statusData.interval_minutes} min` : '—'}</Text>
                  <HStack>
                    <Switch size="sm" isChecked={!!statusData?.newsletter_enabled} onChange={(e)=> automationToggle.mutate(e.target.checked)} />
                    <Text>Automatické rozesílky</Text>
                  </HStack>
                </HStack>
                {/* Weekly schedule detail */}
                <Box mt={3} color={textSecondary} fontSize="sm">
                  {statusData?.weekly_day ? (
                    <>
                      <Text>
                        <b>Týdenní přehled:</b> {statusData?.weekly_enabled ? 'Zapnuto' : 'Vypnuto'}
                        {statusData?.weekly_enabled ? (
                          <> — {({sun:'Neděle', mon:'Pondělí', tue:'Úterý', wed:'Středa', thu:'Čtvrtek', fri:'Pátek', sat:'Sobota'} as any)[statusData.weekly_day as any]}
                          {' '}{String(statusData?.weekly_hour ?? 9).padStart(2,'0')}:00</>
                        ) : null}
                      </Text>
                      {statusData?.weekly_next_scheduled ? (
                        <Text>
                          <b>Příští týdenní odeslání:</b> {format(new Date(statusData.weekly_next_scheduled), 'd. M. yyyy HH:mm', { locale: cs })}
                        </Text>
                      ) : null}
                    </>
                  ) : null}
                </Box>
                {statusData?.next_approximate ? (
                  <Text color="gray.600" fontSize="sm" mt={2}>
                    Další automatický newsletter za {(() => {
                      const diff = new Date(statusData.next_approximate).getTime() - Date.now();
                      if (diff <= 0) return 'méně než minutu';
                      const mins = Math.floor(diff / 60000);
                      const hrs = Math.floor(mins / 60);
                      const rem = mins % 60;
                      return hrs > 0 ? `${hrs} h ${rem} min` : `${mins} min`;
                    })()}
                  </Text>
                ) : null}
                {statusData?.sample_recipients?.length ? (
                  <Box mt={3}>
                    <Text color="gray.600" fontSize="sm">Ukázka příjemců ({statusData.sample_recipients.length}): {statusData.sample_recipients.join(', ')}</Text>
                  </Box>
                ) : null}
              </Box>
              <Box bg={cardBg} borderRadius="lg" boxShadow="sm" p={4} mb={6}>
                <HStack justify="space-between" align="center" mb={3}>
                  <Heading size="md">Typy e‑mailů</Heading>
                  <HelpTooltipCard
                    label="Jaké typy se posílají"
                    title="Typy newsletterů a komu chodí"
                    items={[
                      'Každý odběratel si v preferencích volí, jaké typy e‑mailů chce dostávat (týdenní přehled, zápasy, výsledky, novinky, akce).',
                      'Počet příjemců u každého typu vychází z aktivních odběratelů, kteří mají daný typ povolený.',
                      'Přes tlačítko „Detail“ uvidíte náhled konkrétního e‑mailu a můžete zkontrolovat předmět i obsah před hromadným odesláním.',
                    ]}
                  />
                </HStack>
                <VStack align="stretch" spacing={3}>
                  {(['weekly','matches','scores','blogs','events'] as MailType[]).map((t)=>{
                    const count = recipientsForType(t).length;
                    const enabled = t === 'weekly' ? !!settings?.enable_weekly : t === 'matches' ? !!settings?.enable_match_reminders : t === 'scores' ? !!settings?.enable_results : undefined;
                    return (
                      <Flex key={t} align="center" justify="space-between" p={3} borderWidth="1px" borderRadius="md" _hover={{ bg: hoverBg }}>
                        <HStack spacing={3}>
                          <Text fontWeight="600">{mailTypeLabel[t]}</Text>
                          {enabled !== undefined && (
                            <Badge colorScheme={enabled ? 'green' : 'gray'}>{enabled ? 'Zapnuto' : 'Vypnuto'}</Badge>
                          )}
                        </HStack>
                        <HStack spacing={4}>
                          <Text color={textSecondary}>Příjemci: <b>{count}</b></Text>
                          {typePreview[t]?.subject ? (
                            <Badge colorScheme="blue" title="Předmět připraveného e‑mailu">
                              {typePreview[t]!.subject}
                            </Badge>
                          ) : null}
                          <Button size="sm" onClick={()=> openDetails(t)}>Detail</Button>
                        </HStack>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            </TabPanel>
            <TabPanel p={0}>
              {/* Scheduling controls */}
              <Box bg={cardBg} borderRadius="lg" boxShadow="sm" p={4} mb={6}>
                <HStack justify="space-between" align="center" mb={1}>
                  <Heading size="md">Plánování rozesílek</Heading>
                  <HelpTooltipCard
                    label="Jak funguje plánování"
                    title="Kdy se newslettery automaticky posílají"
                    items={[
                      'Nastavte den a čas pro týdenní přehled a předstih pro připomínky zápasů – systém pak běží automaticky na pozadí.',
                      'Tiché hodiny brání odesílání výsledkových e‑mailů v nevhodnou dobu (např. v noci).',
                      'Aby se e‑maily odesílaly, musí být zároveň zapnuté typy rozesílek v Nastavení a povolené „Automatické rozesílky“ na kartě Stav.',
                    ]}
                  />
                </HStack>
                <Text color="gray.600" mb={4}>Nastavte, kdy se automaticky posílají jednotlivé typy newsletterů.</Text>
                {settingsQuery.isLoading ? (
                  <HStack color="gray.600"><Spinner size="sm" /> <Text>Načítám nastavení…</Text></HStack>
                ) : (
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                      <Text fontWeight="600">Týdenní přehled</Text>
                      <Switch isChecked={enableWeekly} onChange={(e)=> setEnableWeekly(e.target.checked)} />
                    </HStack>
                    <HStack spacing={3}>
                      <FormControl maxW="220px">
                        <FormLabel>Den v týdnu</FormLabel>
                        <Select value={weeklyDay} onChange={(e)=> setWeeklyDay(e.target.value as any)}>
                          <option value="mon">Pondělí</option>
                          <option value="tue">Úterý</option>
                          <option value="wed">Středa</option>
                          <option value="thu">Čtvrtek</option>
                          <option value="fri">Pátek</option>
                          <option value="sat">Sobota</option>
                          <option value="sun">Neděle</option>
                        </Select>
                      </FormControl>
                      <FormControl maxW="200px">
                        <FormLabel>Čas odeslání</FormLabel>
                        <Input type="time" step={900} value={weeklyTime} onChange={(e)=> setWeeklyTime(e.target.value)} />
                      </FormControl>
                    </HStack>

                    <Box h="1px" bg={useColorModeValue('gray.200', 'gray.700')} my={2} />

                    <HStack justify="space-between">
                      <Text fontWeight="600">Připomínky zápasů</Text>
                      <Switch isChecked={enableMatchReminders} onChange={(e)=> setEnableMatchReminders(e.target.checked)} />
                    </HStack>
                    <HStack spacing={3}>
                      <FormControl maxW="220px">
                        <FormLabel>Odeslat před (hodin)</FormLabel>
                        <Input type="number" min={1} max={168} value={reminderLead} onChange={(e)=> setReminderLead(Math.max(1, Math.min(168, Number(e.target.value)||0)))} />
                        <FormHelperText>Výchozí 48 h před výkopem. Systém posílá i upozornění v den zápasu.</FormHelperText>
                      </FormControl>
                    </HStack>

                    <Box h="1px" bg={useColorModeValue('gray.200', 'gray.700')} my={2} />

                    <HStack justify="space-between">
                      <Text fontWeight="600">Výsledky po zápase</Text>
                      <Switch isChecked={enableResults} onChange={(e)=> setEnableResults(e.target.checked)} />
                    </HStack>
                    <HStack spacing={3}>
                      <FormControl maxW="200px">
                        <FormLabel>Tiché hodiny od</FormLabel>
                        <Input type="time" step={900} value={quietStartTime} onChange={(e)=> setQuietStartTime(e.target.value)} />
                      </FormControl>
                      <FormControl maxW="200px">
                        <FormLabel>Tiché hodiny do</FormLabel>
                        <Input type="time" step={900} value={quietEndTime} onChange={(e)=> setQuietEndTime(e.target.value)} />
                        <FormHelperText>E-maily s výsledky se neposílají v tomto intervalu.</FormHelperText>
                      </FormControl>
                    </HStack>

                    <HStack pt={2}>
                      <Button colorScheme="blue" onClick={()=> saveScheduleMutation.mutate()} isLoading={saveScheduleMutation.isLoading}>Uložit plánování</Button>
                    </HStack>
                  </VStack>
                )}
              </Box>
            </TabPanel>

            {/* Analytics tab */}
            <TabPanel p={0}>
              <Box bg={cardBg} borderRadius="lg" boxShadow="sm" p={4} mb={6}>
                <Heading size="md" mb={3}>Dodání a interakce</Heading>
                {statsLoading ? (
                  <HStack color="gray.600"><Spinner size="sm" /> <Text>Načítám statistiky…</Text></HStack>
                ) : recentStats.length === 0 ? (
                  <Text color="gray.600">Zatím nejsou k dispozici žádné záznamy.</Text>
                ) : (
                  <>
                    <HStack mb={3} spacing={3} align="center">
                      <DateRangePicker
                        from={statsFrom || undefined}
                        to={statsTo || undefined}
                        onChange={(fromValue, toValue) => {
                          setStatsFrom((fromValue || '').trim());
                          setStatsTo((toValue || '').trim());
                        }}
                        size="sm"
                      />
                      {(statsFrom || statsTo) && (
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="red"
                          onClick={() => { setStatsFrom(''); setStatsTo(''); }}
                          borderRadius="md"
                        >
                          Vymazat filtr
                        </Button>
                      )}
                    </HStack>
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Čas</Th>
                            <Th>Předmět</Th>
                            <Th>Příjemce</Th>
                            <Th isNumeric>Otevření</Th>
                            <Th isNumeric>Kliknutí</Th>
                            <Th isNumeric>Spam</Th>
                            <Th isNumeric>Odhlášení</Th>
                            <Th>Stav</Th>
                            <Th>Detail</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredStats.slice(0, 50).map((row) => (
                            <Tr key={row.id}>
                              <Td>{format(new Date(row.created_at), 'd. M. yyyy HH:mm', { locale: cs })}</Td>
                              <Td>{row.subject}</Td>
                              <Td>{row.recipient}</Td>
                              <Td isNumeric>{row.opens}</Td>
                              <Td isNumeric>{row.clicks}</Td>
                              <Td isNumeric>{row.spam}</Td>
                              <Td isNumeric>{row.unsubs}</Td>
                              <Td>
                                <Badge colorScheme={row.status === 'failed' ? 'red' : 'green'}>{row.status}</Badge>
                              </Td>
                              <Td>
                                <Button size="xs" variant="outline" onClick={() => { setActiveLog(row); setEventsModalOpen(true); }}>Detail</Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Subscribers table */}
        <Box bg={cardBg} borderRadius="lg" boxShadow="sm" p={4} mb={6}>
          <InputGroup maxW="md" mb={4}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Hledat podle e-mailu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th w="40px">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        filteredSubscribers.length > 0 &&
                        selectedSubscribers.length === filteredSubscribers.length
                      }
                    />
                  </Th>
                  <Th>E-mail</Th>
                  <Th>Stav</Th>
                  <Th>Datum registrace</Th>
                  <Th>Poslední změna</Th>
                  <Th w="120px">Akce</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      Načítání...
                    </Td>
                  </Tr>
                ) : filteredSubscribers.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                      {searchTerm ? 'Žádní odběratelé nebyli nalezeni' : 'Žádní odběratelé'}
                    </Td>
                  </Tr>
                ) : (
                  filteredSubscribers.map((subscriber) => (
                    <Tr key={subscriber.id}>
                      <Td>
                        <input
                          type="checkbox"
                          checked={selectedSubscribers.includes(subscriber.id)}
                          onChange={(e) =>
                            handleSelectSubscriber(subscriber.id, e.target.checked)
                          }
                        />
                      </Td>
                      <Td>{subscriber.email}</Td>
                      <Td>
                        <Badge
                          colorScheme={subscriber.is_active ? 'green' : 'gray'}
                        >
                          {subscriber.is_active ? 'Aktivní' : 'Neaktivní'}
                        </Badge>
                      </Td>
                      <Td>
                        {(() => {
                          const prefs = (subscriber as any).preferences;
                          if (prefs && Object.keys(prefs).length > 0) {
                            return Object.keys(prefs).map((k) =>
                              prefs[k] ? <Badge key={k} mr={1} colorScheme="blue">{k}</Badge> : null
                            );
                          }
                          return <Text color="gray.500">Žádné</Text>;
                        })()}
                      </Td>
                      <Td>{formatDate(subscriber.created_at)}</Td>
                      <Td>{formatDate(subscriber.updated_at)}</Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label={subscriber.is_active ? 'Deaktivovat' : 'Aktivovat'}>
                            <span>
                              <Switch
                                colorScheme="green"
                                isChecked={subscriber.is_active}
                                onChange={(e) =>
                                  toggleStatusMutation.mutate({
                                    id: subscriber.id,
                                    isActive: e.target.checked,
                                  })
                                }
                                isDisabled={toggleStatusMutation.isLoading}
                              />
                            </span>
                          </Tooltip>
                          <Tooltip label="Smazat">
                            <span>
                              <IconButton
                                aria-label="Smazat odběratele"
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={async () => {
                                  const ok = await confirm({
                                    title: 'Smazat odběratele',
                                    message: 'Opravdu chcete smazat tohoto odběratele?',
                                    confirmText: 'Smazat',
                                    cancelText: 'Zrušit',
                                    isDanger: true,
                                  });
                                  if (!ok) return;
                                  deleteMutation.mutate(subscriber.id);
                                }}
                              />
                            </span>
                          </Tooltip>
                          <Tooltip label="Upravit preference">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingSubscriber(subscriber);
                                setEditingPrefs((subscriber as any).preferences || {});
                                setPrefsModalOpen(true);
                              }}
                            >
                              Upravit
                            </Button>
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Box>

      {/* Send Newsletter Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" overflowY="auto">
          <ModalHeader>Odeslat newsletter</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Newsletter bude odeslán všem aktivním odběratelům ({subscribers.filter(s => s.is_active).length}).
              </Text>

              <FormControl>
                <FormLabel>Typ</FormLabel>
                <Select value={sendMode} onChange={(e)=> setSendMode(e.target.value as any)}>
                  <option value="custom">Vlastní obsah</option>
                  <option value="matches">Šablona: Zápasy</option>
                  <option value="scores">Šablona: Výsledky</option>
                  <option value="events">Šablona: Akce</option>
                  <option value="blogs">Šablona: Novinky</option>
                  <option value="weekly">Šablona: Týdenní přehled</option>
                </Select>
                <FormHelperText>Zvolte mezi vlastním obsahem nebo automatickou šablonou.</FormHelperText>
              </FormControl>
              
              {sendMode === 'custom' && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Předmět</FormLabel>
                    <Input
                      placeholder="Předmět zprávy"
                      value={newsletterData.subject}
                      onChange={(e) =>
                        setNewsletterData({ ...newsletterData, subject: e.target.value })
                      }
                    />
                  </FormControl>
              
                  <FormControl isRequired>
                    <FormLabel>Obsah zprávy</FormLabel>
                    <Textarea
                      placeholder="Zde napište obsah newsletteru..."
                      value={newsletterData.content}
                      onChange={(e) =>
                        setNewsletterData({ ...newsletterData, content: e.target.value })
                      }
                      rows={10}
                    />
                    <FormHelperText>
                      Pro formátování textu můžete použít HTML značky.
                    </FormHelperText>
                  </FormControl>
                </>
              )}

              {sendMode !== 'custom' && (
                <>
                  <FormControl>
                    <FormLabel>Filtr soutěží (volitelné)</FormLabel>
                    {compOptions.length > 0 ? (
                      <VStack align="stretch" spacing={2} maxH="220px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
                        <HStack>
                          <Button size="xs" variant="outline" onClick={selectAllComps}>Vybrat vše</Button>
                          <Button size="xs" variant="ghost" onClick={clearComps}>Zrušit vše</Button>
                        </HStack>
                        {compOptions.map((o) => {
                          const checked = selectedCompCodes.has(o.code.toLowerCase()) || selectedCompCodes.has(o.code);
                          return (
                            <HStack key={o.code} justify="space-between">
                              <Text>{o.name}</Text>
                              <Switch isChecked={checked} onChange={(e)=> toggleComp(o.code, e.target.checked)} />
                            </HStack>
                          );
                        })}
                      </VStack>
                    ) : (
                      <>
                        <Input placeholder="NAPŘ. KP, I.A, I.B" value={competitions} onChange={(e)=> setCompetitions(e.target.value)} />
                        <FormHelperText>Čárkou oddělený seznam kódů soutěží.</FormHelperText>
                      </>
                    )}
                  </FormControl>
                  <HStack>
                    <Button variant="outline" onClick={async ()=>{
                      setPreviewLoading(true);
                      try {
                        const prefs: any = {};
                        if (sendMode === 'weekly') { prefs.blogs = true; prefs.events = true; prefs.matches = true; prefs.scores = true; } else { (prefs as any)[sendMode] = true; }
                        if (competitions.trim()) { prefs.competitions = competitions.trim(); }
                        const res = await previewNewsletter({ preferences: prefs });
                        setPreviewSubject(res.subject);
                        setPreviewHtml(res.html);
                      } finally {
                        setPreviewLoading(false);
                      }
                    }}>Náhled šablony</Button>
                    {previewLoading && <Spinner size="sm" />}
                    {previewSubject && <Badge colorScheme="blue">{previewSubject}</Badge>}
                  </HStack>
                  <Box mt={2} p={3} bg={useColorModeValue('gray.50', 'gray.900')} borderRadius="md" borderWidth="1px">
                    <Box
                      className="ql-editor"
                      p={3}
                      borderRadius="md"
                      borderWidth="1px"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml || '<em>Náhled se zobrazí zde</em>') }}
                    />
                  </Box>
                  <Box mt={4} p={4} bg={useColorModeValue('gray.50', 'gray.900')} borderRadius="md">
                    <Text fontWeight="bold" mb={2}>Náhled:</Text>
                    <Box
                      border="1px"
                      borderColor="gray.200"
                      p={4}
                      borderRadius="md"
                      bg={cardBg}
                      className="ql-editor"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml || '<em>Náhled se zobrazí zde</em>') }}
                    />
                  </Box>
                </>
              )}
              
              </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Zrušit
            </Button>
            {sendMode === 'custom' ? (
              <Button
                colorScheme="blue"
                onClick={handleSendNewsletter}
                isLoading={sendNewsletterMutation.isLoading}
                isDisabled={!newsletterData.subject || !newsletterData.content}
                leftIcon={<EmailIcon />}
              >
                Odeslat newsletter
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                onClick={async ()=>{
                  try {
                    await sendNewsletterDigest(sendMode as DigestType, competitions.trim() || undefined);
                    toast({ title: 'Digest odeslán', status: 'success' });
                    onClose();
                    setPreviewHtml(''); setPreviewSubject(''); setCompetitions(''); setSendMode('custom');
                  } catch (e: any) {
                    toast({ title: 'Chyba při odeslání', description: e?.response?.data?.error || e?.message, status: 'error' });
                  }
                }}
                leftIcon={<EmailIcon />}
              >
                Odeslat digest
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={detailsOpen} onClose={closeDetails} size="5xl">
        <ModalOverlay />
        <ModalContent maxW="95vw" maxH="90vh" overflowY="auto">
          <ModalHeader>
            {activeType ? `Detail: ${mailTypeLabel[activeType]}` : 'Detail'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <HStack spacing={4} align="flex-end">
                <FormControl maxW="420px">
                  <FormLabel>Filtr soutěží (volitelné)</FormLabel>
                  {compOptions.length > 0 ? (
                    <VStack align="stretch" spacing={2} maxH="220px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
                      <HStack>
                        <Button size="xs" variant="outline" onClick={detailsSelectAllComps}>Vybrat vše</Button>
                        <Button size="xs" variant="ghost" onClick={detailsClearComps}>Zrušit vše</Button>
                      </HStack>
                      {compOptions.map((o) => {
                        const checked = detailsSelectedCompCodes.has(o.code.toLowerCase()) || detailsSelectedCompCodes.has(o.code);
                        return (
                          <HStack key={o.code} justify="space-between">
                            <Text>{o.name}</Text>
                            <Switch isChecked={checked} onChange={(e)=> toggleDetailsComp(o.code, e.target.checked)} />
                          </HStack>
                        );
                      })}
                    </VStack>
                  ) : (
                    <Input placeholder="NAPŘ. KP, I.A, I.B" value={detailsCompetitions} onChange={(e)=> setDetailsCompetitions(e.target.value)} />
                  )}
                </FormControl>
                <Button onClick={async()=>{ if(!activeType) return; setDetailsLoading(true); try { await loadPreviewForType(activeType, detailsCompetitions); } finally { setDetailsLoading(false); } }} isLoading={detailsLoading}>Aktualizovat náhled</Button>
                {activeType && typePreview[activeType]?.subject && (
                  <Badge colorScheme="blue">{typePreview[activeType]!.subject}</Badge>
                )}
                <Button colorScheme="blue" variant="solid" isLoading={sendNowLoading} onClick={async()=>{
                  if(!activeType) return;
                  const ok = await confirm({
                    title: 'Odeslat nyní',
                    message: `Odeslat "${mailTypeLabel[activeType]}" nyní? E‑mail bude odeslán všem aktivním odběratelům.`,
                    confirmText: 'Odeslat',
                    cancelText: 'Zrušit',
                    isDanger: true,
                  });
                  if(!ok) return;
                  try {
                    setSendNowLoading(true);
                    await sendNewsletterDigest(activeType as DigestType, (detailsCompetitions || '').trim() || undefined);
                    toast({ title: 'Digest odeslán', status: 'success' });
                  } catch (e: any) {
                    toast({ title: 'Chyba při odeslání', description: e?.response?.data?.error || e?.message, status: 'error' });
                  } finally {
                    setSendNowLoading(false);
                  }
                }}>Odeslat nyní</Button>
                <Button variant="outline" onClick={()=>{ if(!activeType) return; exportRecipientsCSV(activeType, detailsCompetitions); }}>Export CSV</Button>
              </HStack>
              <Box p={3} bg={useColorModeValue('gray.50', 'gray.900')} borderRadius="md" borderWidth="1px">
                <Box className="ql-editor" bg={cardBg} p={3} borderRadius="md" borderWidth="1px" dangerouslySetInnerHTML={{ __html: sanitizeHtml(activeType ? (typePreview[activeType]?.html || '<em>Náhled se zobrazí zde</em>') : '<em>Náhled se zobrazí zde</em>') }} />
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Příjemci</Heading>
                {(() => {
                  let list: string[] = [];
                  if (activeType) {
                    const typeKey = activeType === 'weekly' ? 'weekly' : activeType;
                    const base = (subscribers as any[]).filter((s: any) => s?.is_active && s?.preferences && s.preferences[typeKey] === true);
                    let filtered = base;
                    const compsInput = (detailsCompetitions || '').trim();
                    if (compsInput && (activeType === 'matches' || activeType === 'scores')) {
                      const comps = compsInput.split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
                      if (comps.length > 0) {
                        filtered = base.filter((s: any) => {
                          const prefs: any = s?.preferences || {};
                          const raw: string = typeof prefs.competitions === 'string' && prefs.competitions
                            ? prefs.competitions
                            : (typeof prefs.categories === 'string' ? prefs.categories : '');
                          const arr = raw.split(',').map((x: string) => x.trim().toLowerCase()).filter(Boolean);
                          if (arr.length === 0) return true;
                          return arr.some((v: string) => comps.includes(v));
                        });
                      }
                    }
                    list = filtered.map((s: any) => s.email);
                  }
                  const shown = list.slice(0, 50);
                  return (
                    <>
                      {shown.length === 0 ? (
                        <Text color="gray.600">Žádní příjemci pro tento typ.</Text>
                      ) : (
                        <VStack align="stretch" spacing={1} maxH="240px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
                          {shown.map((e)=> (<Text key={e} fontFamily="mono">{e}</Text>))}
                          {list.length > shown.length && (
                            <Text color="gray.600">… a dalších {list.length - shown.length}</Text>
                          )}
                        </VStack>
                      )}
                    </>
                  );
                })()}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeDetails}>Zavřít</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* SMTP Test Modal */}
      <Modal isOpen={smtpModal.isOpen} onClose={smtpModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" overflowY="auto">
          <ModalHeader>Otestovat SMTP</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={3} align="flex-end">
                <FormControl>
                  <FormLabel>Host</FormLabel>
                  <Input placeholder="smtp.example.com" value={smtpHost} onChange={(e)=> setSmtpHost(e.target.value)} />
                </FormControl>
                <FormControl maxW="140px">
                  <FormLabel>Port</FormLabel>
                  <Input type="number" placeholder="465" value={smtpPort} onChange={(e)=> setSmtpPort(Number(e.target.value)||0)} />
                </FormControl>
                <FormControl maxW="140px">
                  <FormLabel>&nbsp;</FormLabel>
                  <Checkbox isChecked={smtpTLS} onChange={(e)=> setSmtpTLS(e.target.checked)}>TLS/SSL</Checkbox>
                </FormControl>
              </HStack>
              <HStack spacing={3}>
                <FormControl>
                  <FormLabel>Uživatel</FormLabel>
                  <Input value={smtpUser} onChange={(e)=> setSmtpUser(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Heslo</FormLabel>
                  <InputGroup>
                    <Input type={showSmtpPass ? 'text' : 'password'} value={smtpPass} onChange={(e)=> setSmtpPass(e.target.value)} />
                    <InputRightElement width="4.5rem">
                      <Button h="1.75rem" size="sm" onClick={()=> setShowSmtpPass(v=> !v)}>
                        {showSmtpPass ? 'Skrýt' : 'Zobrazit'}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
              </HStack>
              <HStack spacing={3}>
                <FormControl>
                  <FormLabel>From</FormLabel>
                  <Input placeholder="club@example.com" value={smtpFrom} onChange={(e)=> setSmtpFrom(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>To (kam poslat test)</FormLabel>
                  <Input placeholder="you@example.com" value={smtpTo} onChange={(e)=> setSmtpTo(e.target.value)} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Předmět</FormLabel>
                <Input value={smtpSubject} onChange={(e)=> setSmtpSubject(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Tělo zprávy (HTML)</FormLabel>
                <Textarea rows={6} value={smtpBody} onChange={(e)=> setSmtpBody(e.target.value)} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={smtpModal.onClose}>Zavřít</Button>
            <Button colorScheme="blue" onClick={()=> adminSmtpTestMutation.mutate()} isLoading={adminSmtpTestMutation.isLoading}>Odeslat test</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Test Email Modal */}
      <Modal isOpen={testModal.isOpen} onClose={testModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" overflowY="auto">
          <ModalHeader>Odeslat testovací e-mail</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.600">
                Testovací e-mail bude odeslán na zadanou adresu. Pokud pole necháte prázdné, odešleme na výchozí administrátorský e-mail.
              </Text>
              <FormControl>
                <FormLabel>E-mail příjemce (volitelně)</FormLabel>
                <Input
                  type="email"
                  placeholder="např. admin@priklad.cz"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Více e-mailů (oddělené čárkou)</FormLabel>
                <Input
                  placeholder="user1@priklad.cz, user2@priklad.cz"
                  value={testEmails}
                  onChange={(e) => setTestEmails(e.target.value)}
                />
                <FormHelperText>Pokud vyplníte, použije se toto pole přednostně.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Typ testu</FormLabel>
                <select value={testType} onChange={(e) => setTestType(e.target.value as NewsletterTestType)}>
                  <option value="newsletter">Newsletter</option>
                  <option value="welcome">Uvítací</option>
                  <option value="welcome_back">Uvítací (návrat)</option>
                  <option value="blogs">Blogy (digest)</option>
                  <option value="events">Události (digest)</option>
                  <option value="matches">Zápasy (digest)</option>
                  <option value="scores">Výsledky (digest)</option>
                  <option value="weekly">Týdenní přehled</option>
                </select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={testModal.onClose}>
              Zavřít
            </Button>
            <Button colorScheme="blue" onClick={() => sendTestMutation.mutate()} isLoading={sendTestMutation.isLoading}>
              Odeslat test
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Preferences Modal */}
      <Modal isOpen={prefsModalOpen} onClose={() => setPrefsModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upravit preference</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Týdenní přehled</FormLabel>
                <Switch isChecked={!!editingPrefs['weekly']} onChange={(e) => setEditingPrefs({ ...editingPrefs, weekly: e.target.checked })} />
              </FormControl>
              <FormControl>
                <FormLabel>Nadcházející zápasy</FormLabel>
                <Switch isChecked={!!editingPrefs['matches']} onChange={(e) => setEditingPrefs({ ...editingPrefs, matches: e.target.checked })} />
              </FormControl>
              <FormControl>
                <FormLabel>Blog</FormLabel>
                <Switch isChecked={!!editingPrefs['blogs']} onChange={(e) => setEditingPrefs({ ...editingPrefs, blogs: e.target.checked })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setPrefsModalOpen(false)}>Zrušit</Button>
            <Button
              colorScheme="blue"
              onClick={() =>
                editingSubscriber
                  ? updatePrefsMutation.mutate({ id: editingSubscriber.id, prefs: editingPrefs })
                  : undefined
              }
              isLoading={updatePrefsMutation.isLoading}
              isDisabled={!editingSubscriber}
            >
              Uložit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Subscriber Modal */}
      <Modal isOpen={addSubscriberModal.isOpen} onClose={addSubscriberModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Přidat odběratele</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>E-mail odběratele</FormLabel>
                <Input
                  value={newSubscriberEmail}
                  onChange={(e) => setNewSubscriberEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </FormControl>

              <FormControl>
                <FormLabel>IČO (volitelné)</FormLabel>
                <InputGroup>
                  <Input
                    value={newSubscriberICO}
                    onChange={(e) => handleICOChange(e.target.value)}
                    placeholder="12345678"
                    maxLength={8}
                  />
                  <InputRightElement width="4.5rem">
                    {icoSearching ? (
                      <Spinner size="sm" />
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => {
                          if (newSubscriberICO.length === 8 && /^\d+$/.test(newSubscriberICO)) {
                            setIcoSearching(true);
                            icoSearchMutation.mutate(newSubscriberICO);
                          }
                        }}
                        isDisabled={newSubscriberICO.length !== 8 || !/^\d+$/.test(newSubscriberICO)}
                      >
                        Hledat
                      </Button>
                    )}
                  </InputRightElement>
                </InputGroup>
                {icoSearchResult && (
                  <Box mt={2} p={3} borderWidth="1px" borderRadius="md" bg={icoSearchResult.found ? "green.50" : "red.50"}>
                    {icoSearchResult.found ? (
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="600">Nalezeno:</Text>
                        <Text><strong>Název:</strong> {icoSearchResult.name}</Text>
                        <Text><strong>IČO:</strong> {icoSearchResult.ico}</Text>
                        {icoSearchResult.dic && <Text><strong>DIČ:</strong> {icoSearchResult.dic}</Text>}
                        <Text><strong>Adresa:</strong> {icoSearchResult.address}, {icoSearchResult.city}</Text>
                      </VStack>
                    ) : (
                      <Text color="red.600">Subjekt s IČO {newSubscriberICO} nebyl nalezen</Text>
                    )}
                  </Box>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Preference odběru</FormLabel>
                <VStack align="start" spacing={2}>
                  <Checkbox
                    isChecked={!!newSubscriberPrefs.weekly}
                    onChange={(e) => setNewSubscriberPrefs({ ...newSubscriberPrefs, weekly: e.target.checked })}
                  >
                    Týdenní přehled
                  </Checkbox>
                  <Checkbox
                    isChecked={!!newSubscriberPrefs.matches}
                    onChange={(e) => setNewSubscriberPrefs({ ...newSubscriberPrefs, matches: e.target.checked })}
                  >
                    Nadcházející zápasy
                  </Checkbox>
                  <Checkbox
                    isChecked={!!newSubscriberPrefs.scores}
                    onChange={(e) => setNewSubscriberPrefs({ ...newSubscriberPrefs, scores: e.target.checked })}
                  >
                    Výsledky zápasů
                  </Checkbox>
                  <Checkbox
                    isChecked={!!newSubscriberPrefs.blogs}
                    onChange={(e) => setNewSubscriberPrefs({ ...newSubscriberPrefs, blogs: e.target.checked })}
                  >
                    Novinky a články
                  </Checkbox>
                  <Checkbox
                    isChecked={!!newSubscriberPrefs.events}
                    onChange={(e) => setNewSubscriberPrefs({ ...newSubscriberPrefs, events: e.target.checked })}
                  >
                    Akce a události
                  </Checkbox>
                </VStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={addSubscriberModal.onClose}>
              Zrušit
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateSubscriber}
              isLoading={createSubscriberMutation.isLoading}
              isDisabled={!newSubscriberEmail.trim()}
            >
              Přidat odběratele
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
}
